import { useCallback } from 'react'
import {
  createStateUpdater,
  handleResetFocus,
  resetState,
  clickCreateSideEffect,
  createHandleCreate,
  handleError,
  createHandleSubmit,
  goBackOneModal,
  handleClassNameChange,
  handleGradeChange,
  createHandleSaveEdit,
  createHandleViewClass,
  createHandleCancel,
  handleDeleteSubmit,
  handleEditSubmit,
  createHandleDelete,
  createValidateForm,
  createHandleSiteChange,
} from 'experienceComponents/CreateInstanceShared/sharedStateManagement.js'
import {
  courseFilterConfig,
  courseProgramConfig,
  courseViewClassConfig,
} from './courseConfig.js'
import { checkBlockingValidations } from './data'

// Export the configured functions for CreateCourse
export const handleCreate = createHandleCreate(courseFilterConfig)
export const handleSubmit = createHandleSubmit(
  courseProgramConfig,
  'Course was not created',
  'ERROR CREATING CLASS:',
)

export const handleSaveEdit = createHandleSaveEdit('ERROR EDITING CLASS:')
export const handleViewClass = createHandleViewClass(courseViewClassConfig)
export const handleCancel = createHandleCancel()
export const handleSiteChange = createHandleSiteChange()
export const handleDelete = createHandleDelete('ERROR DELETING CLASS:')
export const validateForm = createValidateForm(checkBlockingValidations)

// Custom hook that creates all callback handlers
export const useCreateCourseHandlers = (state, props, updateState) => {
  const resetStateCallback = useCallback(() => {
    const result = resetState(state, props)
    updateState(result.newState, result.sideEffects)
  }, [state, props, updateState])

  const handleCreateCallback = useCallback(() => {
    const result = handleCreate(state, props)
    updateState(result.newState, result.sideEffects)
  }, [state, props, updateState])

  const handleSubmitCallback = useCallback(async () => {
    const result = await handleSubmit(state, props)
    updateState(result.newState, result.sideEffects)
  }, [state, props, updateState])

  const goBackOneModalCallback = useCallback(() => {
    const result = goBackOneModal(state)
    updateState(result.newState, result.sideEffects)
  }, [state, updateState])

  const handleClassNameChangeCallback = useCallback(
    e => {
      const result = handleClassNameChange(e, state)
      updateState(result.newState, result.sideEffects)
    },
    [state, updateState],
  )

  const handleGradeChangeCallback = useCallback(
    (e, courses = []) => {
      const result = handleGradeChange(e, courses, state)
      updateState(result.newState, result.sideEffects)
    },
    [state, updateState],
  )

  const handleSiteChangeCallback = useCallback(
    (e, sites = []) => {
      const result = handleSiteChange(e, sites, state, props)
      updateState(result.newState, result.sideEffects)
    },
    [state, props, updateState],
  )

  const handleSaveEditCallback = useCallback(async () => {
    const result = await handleSaveEdit(state, props)
    updateState(result.newState, result.sideEffects)
  }, [state, props, updateState])

  const handleViewClassCallback = useCallback(() => {
    handleViewClass(state, props)
  }, [state, props])

  const handleCancelCallback = useCallback(() => {
    const result = handleCancel(state, props)
    updateState(result.newState, result.sideEffects)
  }, [state, props, updateState])

  const handleDeleteSubmitCallback = useCallback(() => {
    const result = handleDeleteSubmit(state)
    updateState(result.newState, result.sideEffects)
  }, [state, updateState])

  const handleEditSubmitCallback = useCallback(() => {
    const result = handleEditSubmit(state)
    updateState(result.newState, result.sideEffects)
  }, [state, updateState])

  const handleDeleteCallback = useCallback(async () => {
    const result = await handleDelete(state, props)
    updateState(result.newState, result.sideEffects)
  }, [state, props, updateState])

  const validateFormCallback = useCallback(async () => {
    const result = await validateForm(state, props)
    updateState(result.newState)
  }, [state, props, updateState])

  return {
    resetStateCallback,
    handleCreateCallback,
    handleSubmitCallback,
    goBackOneModalCallback,
    handleClassNameChangeCallback,
    handleGradeChangeCallback,
    handleSiteChangeCallback,
    handleSaveEditCallback,
    handleViewClassCallback,
    handleCancelCallback,
    handleDeleteSubmitCallback,
    handleEditSubmitCallback,
    handleDeleteCallback,
    validateFormCallback,
  }
}

// Export other functions that don't need configuration
export {
  createStateUpdater,
  handleResetFocus,
  resetState,
  clickCreateSideEffect,
  handleError,
  goBackOneModal,
  handleClassNameChange,
  handleGradeChange,
  handleDeleteSubmit,
  handleEditSubmit,
}
